import React, { useMemo } from "react";
import type { TicketPurchaseState } from "../../store/ephemeral";
import { ticketPurchaseSlice, useEphemeralSelector } from "../../store/ephemeral";
import { getUIColorPalette } from "../../theme/ColorPalette";

interface TicketPurchaseStepperProps { "isDarkUi": boolean }

const STEPS: {
    "id":    TicketPurchaseState["step"];
    "label": string;
}[] = [
    {
        "id":    "selection",
        "label": "Billets",
    },
    {
        "id":    "summary",
        "label": "Résumé",
    },
    {
        "id":    "payment",
        "label": "Paiement",
    },
    {
        "id":    "confirmation",
        "label": "Confirmation",
    },
];

export const TicketPurchaseStepper: React.FC<TicketPurchaseStepperProps> = ( { isDarkUi } ) => {
    const palette = getUIColorPalette( isDarkUi );
    const step = useEphemeralSelector( ticketPurchaseSlice, ( s: TicketPurchaseState ) => s.step );
    const activeIndex = useMemo( () => STEPS.findIndex( ( s ) => s.id === step ), [ step ] );

    return (
        <div
            className="border-b p-[12px]"
            style={{ "borderColor": palette.overlayBorder }}
        >
            <div className="relative flex items-start justify-between">
                {STEPS.map( ( s, index ) => {
                    const isActive = index <= activeIndex;
                    const isCurrent = index === activeIndex;

                    return (
                        <div
                            className="relative z-10 flex flex-1 flex-col items-center"
                            key={s.id}
                        >
                            <div
                                className="mb-1.5 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
                                style={{
                                    "backgroundColor": palette.modalCardBackground,
                                    "border":          `2.5px solid ${ isCurrent || isActive ? palette.brandPrimary : palette.overlayBorder }`,
                                    "color":           isCurrent || isActive ? palette.brandPrimary : palette.overlayTextSecondary,
                                }}
                            >
                                {index + 1}
                            </div>
                            <span
                                className="text-center text-[10px] font-medium leading-none"
                                style={{
                                    "color": isCurrent || isActive
                                        ? palette.modalCardTextPrimary
                                        : palette.overlayTextSecondary,
                                }}
                            >
                                {s.label}
                            </span>
                        </div>
                    );
                } )}
                <div
                    className="absolute top-3 z-0 flex h-0.5 items-center"
                    style={{
                        "left":  `${ 100 / ( 2 * STEPS.length ) }%`,
                        "right": `${ 100 / ( 2 * STEPS.length ) }%`,
                    }}
                >
                    <div className="relative h-full w-full">
                        <div
                            className="absolute inset-0"
                            style={{ "backgroundColor": palette.overlayBorder }}
                        />
                        <div
                            className="absolute left-0 top-0 h-full transition-all"
                            style={{
                                "width":           `${ ( activeIndex / ( STEPS.length - 1 ) ) * 100 }%`,
                                "backgroundColor": palette.brandPrimary,
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
